/**
 * Structured browser logger facade (mirrors the Explorer app's approach).
 *
 * The application emits diagnostic and error telemetry through these helpers
 * rather than raw `console.*` or bare `newrelic.*` calls. Each helper writes to
 * **two channels**:
 *
 *   1. `console.*` — keeps DevTools rich for local development.
 *   2. The official New Relic Browser API (`window.newrelic.log`,
 *      `window.newrelic.noticeError`) — emits structured events with first-class
 *      queryable `customAttributes`.
 *
 * `window.newrelic` is optional (see `types/newrelic.d.ts`): every NR call is
 * null-checked here, so telemetry safely no-ops while the agent is still
 * loading instead of throwing a `ReferenceError` during early boot.
 *
 * Usage:
 *
 *   logError("Failed to fetch researcher", err, { researcherId: id })
 *   logWarn("Could not load journal catalog", catalogErr)
 *   logInfo("Salesforce session refreshed", { sfInstance: instanceUrl })
 */

/**
 * NR custom attribute values must be primitives — objects/arrays are silently
 * dropped or coerced. Mirrors the `NewRelicAttributes` type in
 * `types/newrelic.d.ts`.
 */
export type LogAttributes = Record<string, string | number | boolean | null>;

/**
 * Format any value as a single readable string. For `Error` instances the
 * output includes the first three stack frames and recurses into `error.cause`;
 * the total is hard-capped at 1024 chars to keep NR `errorMessage` cardinality
 * bounded.
 *
 * `depth` bounds the `error.cause` recursion so a cyclic cause chain (e.g.
 * `a.cause = b; b.cause = a`) can't overflow the stack — the 1024-char cap is
 * applied only after the string is built, so it can't stop the recursion. This
 * is a logging path that must never throw.
 */
export function formatLogValue(value: unknown, depth = 0): string {
    if (value instanceof Error) {
        const head = value.message ? `${value.name}: ${value.message}` : value.name;
        const stack = value.stack
            ?.split("\n")
            .slice(1, 4)
            .map(f => f.trim())
            .join(" | ");
        const causeStr =
            value.cause !== undefined && depth < 3
                ? ` | cause=${formatLogValue(value.cause, depth + 1)}`
                : "";
        const out = stack ? `${head} | ${stack}${causeStr}` : `${head}${causeStr}`;
        return out.length > 1024 ? `${out.slice(0, 1021)}...` : out;
    }
    if (value === null) return "null";
    if (value === undefined) return "undefined";
    if (typeof value === "string") return value;
    if (
        typeof value === "number" ||
        typeof value === "boolean" ||
        typeof value === "bigint"
    ) {
        return String(value);
    }
    if (typeof value === "symbol" || typeof value === "function") {
        return String(value);
    }
    try {
        return JSON.stringify(value);
    } catch {
        return String(value);
    }
}

function nrApi(): Window["newrelic"] | undefined {
    return typeof window === "undefined" ? undefined : window.newrelic;
}

function mergeAttrs(
    base: LogAttributes,
    extra?: LogAttributes
): LogAttributes {
    return extra ? { ...base, ...extra } : base;
}

/**
 * Report an error: writes to `console.error` (DevTools) AND to New Relic.
 *
 * - Real `Error` instances go through `newrelic.noticeError`, which produces a
 *   `JavaScriptError` event visible in the NR Errors UI with the supplied
 *   `attributes` attached as queryable custom attributes (plus a `message`
 *   attribute carrying the call-site context).
 * - Non-`Error` values go through `newrelic.log` at level `ERROR` so the detail
 *   still lands in NR Logs.
 */
export function logError(
    message: string,
    error: unknown,
    attributes?: LogAttributes
): void {
    const formatted = formatLogValue(error);
    console.error(`${message}: ${formatted}`, error);

    const nr = nrApi();
    if (!nr) return;

    if (error instanceof Error) {
        nr.noticeError(error, mergeAttrs({ message }, attributes));
    } else {
        nr.log(`${message}: ${formatted}`, {
            level: "ERROR",
            customAttributes: mergeAttrs({ errorValue: formatted }, attributes)
        });
    }
}

/**
 * Emit a warning: writes to `console.warn` (DevTools) AND to NR `Log` events at
 * level `WARN`. Pass an optional `error` value to record cause context; for
 * attribute-only warnings, pass `undefined` as the second argument.
 */
export function logWarn(
    message: string,
    error?: unknown,
    attributes?: LogAttributes
): void {
    const nr = nrApi();
    if (error !== undefined) {
        const formatted = formatLogValue(error);
        console.warn(`${message}: ${formatted}`, error);
        nr?.log(`${message}: ${formatted}`, {
            level: "WARN",
            customAttributes: mergeAttrs({ errorValue: formatted }, attributes)
        });
        return;
    }
    console.warn(message);
    nr?.log(message, {
        level: "WARN",
        customAttributes: attributes
    });
}

/**
 * Emit an informational message: writes to `console.info` (DevTools) AND to NR
 * `Log` events at level `INFO`.
 */
export function logInfo(message: string, attributes?: LogAttributes): void {
    console.info(message);
    nrApi()?.log(message, {
        level: "INFO",
        customAttributes: attributes
    });
}
